import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import { relativeTime } from "../lib/format";
import { Button, Input, EmptyState } from "../components/ui";

export function MessagesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const loadConversations = useCallback(() => {
    apiGet("/conversations")
      .then((data) => setConversations(data.conversations || []))
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">Messages</h1>
      <div className="grid md:grid-cols-[320px_1fr] gap-4 md:h-[70vh]">
        {/* Conversation list — hidden on mobile when a thread is open */}
        <div className={`${id ? "hidden md:block" : "block"} md:border md:border-slate-200 md:rounded-xl md:bg-white overflow-y-auto`}>
          {loadingList ? (
            <p className="text-slate-400 p-4">Loading…</p>
          ) : conversations.length === 0 ? (
            <EmptyState title="No messages yet">
              Message a seller from any listing to start a conversation.
            </EmptyState>
          ) : (
            <ul className="divide-y divide-slate-100">
              {conversations.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/gear/messages/${c.id}`}
                    className={`flex gap-3 p-3 hover:bg-slate-50 ${c.id === id ? "bg-brand-50" : ""}`}
                  >
                    <div className="h-12 w-12 shrink-0 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center text-xl">
                      {c.listingPhoto ? (
                        <img src={c.listingPhoto} alt="" className="h-full w-full object-cover" />
                      ) : (
                        "⚽"
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between gap-2">
                        <span className="font-semibold text-slate-800 truncate">{c.otherPartyName}</span>
                        <span className="text-xs text-slate-400 shrink-0">{relativeTime(c.lastAt)}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{c.listingTitle}</div>
                      <div className="text-sm text-slate-400 truncate">{c.lastMessage}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread */}
        <div className={`${id ? "block" : "hidden md:block"} md:border md:border-slate-200 md:rounded-xl md:bg-white overflow-hidden`}>
          {id ? (
            <Thread key={id} conversationId={id} onSent={loadConversations} onBack={() => navigate("/gear/messages")} />
          ) : (
            <div className="h-full hidden md:flex items-center justify-center text-slate-400">
              Select a conversation
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Thread({ conversationId, onSent, onBack }) {
  const [meta, setMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  const load = useCallback(
    (showSpinner) => {
      if (showSpinner) setLoading(true);
      apiGet(`/conversations/${conversationId}/messages`)
        .then((data) => {
          setMeta(data.conversation);
          setMessages(data.messages || []);
          setError("");
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    },
    [conversationId]
  );

  useEffect(() => {
    load(true);
    // Light polling so replies show up without a refresh.
    const t = setInterval(() => load(false), 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  async function send(e) {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setText("");
    try {
      const { message } = await apiPost(`/conversations/${conversationId}/messages`, { body });
      setMessages((prev) => [...prev, message]);
      onSent?.();
    } catch (err) {
      setError(err.message);
      setText(body); // restore on failure
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col h-[70vh] md:h-full">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-slate-200">
        <button onClick={onBack} className="md:hidden text-slate-500 px-1">
          ←
        </button>
        {meta ? (
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 truncate">{meta.otherPartyName}</div>
            <Link to={`/gear/listing/${meta.listingId}`} className="text-xs text-brand-600 hover:underline truncate block">
              {meta.listingTitle}
            </Link>
          </div>
        ) : (
          <div className="text-slate-400">Loading…</div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {loading ? (
          <p className="text-slate-400 text-center">Loading messages…</p>
        ) : error ? (
          <p className="text-red-600 text-center text-sm">{error}</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                  m.mine
                    ? "bg-brand-600 text-white rounded-br-sm"
                    : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                }`}
              >
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[10px] mt-0.5 ${m.mine ? "text-white/70" : "text-slate-400"}`}>
                  {relativeTime(m.createdAt)}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-slate-200 bg-white safe-bottom">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          autoComplete="off"
        />
        <Button type="submit" disabled={sending || !text.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}
